import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { getServicesManager } from '../../../services';
import { CnfFilterType, ListUserSessionParamsV3, StaticFilterDisjunction, UserSessionsResultItem } from '../../../interfaces/services';
import { startLoading, sessionsLoaded, loadingFailed, clearSessions, updateHasMore, SessionState as ReduxSessionState } from './list-slice';
import { StaticFilter, StaticFilterKeys, SpecialFilter, convertFilterToStaticCnf, DateOptions, convertFilterFromReduxSafe } from '../../../lib/redux/store';

// Constants
const DEFAULT_PAGE_SIZE = 50;
const MIN_DESIRED_ITEMS = 60;
export const MAX_BACKOFF_RETRIES = 2; // Max number of additional backoff fetches after initial fetch
const MONTH_IN_MS = 30 * 24 * 60 * 60 * 1000;
export const MAX_DATE_LOOKBACK_MONTHS = 12; // Maximum lookback period in months
const LOAD_MORE_MONTHS_EXTENSION = 2; // Number of months to extend when manually loading more
const BACKOFF_MULTIPLIERS = [2, 3]; // First retry looks back 2 months, then 3 more months
const INITIAL_LOOKBACK_MONTHS = 1; // Initial lookback period in months

// Extended session state with our additional tracking properties
interface ExtendedSessionState extends ReduxSessionState {
  totalItems?: number;
  backoffRetryCount?: number;
  // Indicates if we've found no results after searching through our initial time window
  // This should only be true if:
  // 1. We have no results
  // 2. We've completed our initial lookback period (1 month by default)
  // 3. We don't have a strict start date (which would prevent looking back further)
  noResultsAfterBackoff?: boolean;
  // Track how many months we've searched back
  monthsSearched?: number;
}

export function useSessionList(props: {
  forcedFilter?: StaticFilter
  forcedStart?: boolean,
  forUser?: boolean
}) {
  const dispatch = useDispatch();
  const sessionState = useSelector((state: RootState) => state.sessionList);
  const reduxFilter = useSelector((state: RootState) => state.filter.value);
  const filter = convertFilterFromReduxSafe(reduxFilter);
  const refreshCount = useSelector((state: RootState) => state.refresh.forceRefresh);
  
  // Track filter changes for automatic refetching
  const previousFilterRef = useRef<string>(JSON.stringify(filter));
  const hasFilterChanged = useRef<boolean>(false);
  
  // Track whether this is the initial mount to prevent unnecessary refetches
  const isInitialMount = useRef<boolean>(true);

  // Track the last processed refresh count to prevent duplicate refreshes
  const lastProcessedRefreshRef = useRef<number>(0);
  
  // Track if we're currently refreshing to prevent simultaneous refreshes
  const isRefreshingRef = useRef<boolean>(false);

  // Track session state enhancement that we don't store in Redux
  const sessionEnhancementsRef = useRef<Map<string, {
    totalItems: number;
    backoffRetryCount: number;
    noResultsAfterBackoff: boolean;
    monthsSearched: number;
  }>>(new Map());

  // Track if we're currently in an auto-fetch cycle to prevent recursion
  const isAutoFetchingRef = useRef<boolean>(false);

  // Compute the filter to use
  const getFilter = useCallback((): StaticFilter => {
    return props.forcedFilter ?? filter;
  }, [props.forcedFilter, filter]);

  const user = useSelector((state: RootState) => state.user.value);

  // Convert filter to CNF format for API
  const getCnf = useCallback((): StaticFilterDisjunction[] => {
    const cnf = convertFilterToStaticCnf(
      getFilter(), 
      [], 
      [SpecialFilter.IS_REVIEWED, SpecialFilter.IS_STARRED, SpecialFilter.PROSPECT_PHONE_OR_NAME]
    );

    if (props.forUser && user?.user_id) {
      cnf.push({'filter_type': CnfFilterType.USER_ACCOUNT, 'negated': false, 'user_account_filters': [{
        user_ids: [user.user_id]
      }]})
    }
    return cnf;
  }, [getFilter, props.forUser, user?.user_id]);

  // Determine if we have a strict start date from top-level filter
  const hasStrictStartDate = useMemo(() => {
    const activeFilter = getFilter();
    
    // Check if using a custom date range
    const usingCustomDate = activeFilter[StaticFilterKeys.DATE_RANGE] === DateOptions.CUSTOM;
    
    // Check if we have a search by name or phone
    const hasNameOrPhoneSearch = 
      activeFilter[StaticFilterKeys.PROSPECT_PHONE_OR_NAME] && 
      activeFilter[StaticFilterKeys.PROSPECT_PHONE_OR_NAME].length > 0;
    
    // Check if we're filtering by review status
    const hasReviewFilter = 
      activeFilter[StaticFilterKeys.IS_REVIEWED] && 
      activeFilter[StaticFilterKeys.IS_REVIEWED].length > 0;
    
    // Consider start date strict if it's forced or if using custom date range
    const isStartDateStrict = 
      (props.forcedFilter && props.forcedStart) || 
      usingCustomDate ||
      hasNameOrPhoneSearch ||
      hasReviewFilter;
    
    return isStartDateStrict;
  }, [getFilter, props.forcedFilter, props.forcedStart]);

  // Calculate the start date to use for fetching
  const getStartDate = useCallback((
    extendByMonths: number = 0, 
    baseStartDate?: Date | null
  ): Date | null => {
    const activeFilter = getFilter();
    
    // Check if we have a search by name or phone (which doesn't use date ranges)
    const hasNameOrPhoneSearch = 
      activeFilter[StaticFilterKeys.PROSPECT_PHONE_OR_NAME] && 
      activeFilter[StaticFilterKeys.PROSPECT_PHONE_OR_NAME].length > 0;
    if (hasNameOrPhoneSearch) return null;
    
    // Check if we're filtering by review status (which doesn't use date ranges)
    const hasReviewFilter = 
      activeFilter[StaticFilterKeys.IS_REVIEWED] && 
      activeFilter[StaticFilterKeys.IS_REVIEWED].length > 0;
    if (hasReviewFilter) return null;
    
    // Check if using a custom date range
    const usingCustomDate = activeFilter[StaticFilterKeys.DATE_RANGE] === DateOptions.CUSTOM;
    
    // If we have a base start date to adjust, use that
    if (baseStartDate) {
      const adjustedDate = new Date(baseStartDate.getTime());
      adjustedDate.setMonth(adjustedDate.getMonth() - extendByMonths);
      return adjustedDate;
    }
    
    // If using custom date and we have a start date, use that
    if (usingCustomDate) {
      const customStartDate = activeFilter[StaticFilterKeys.START_DATE];
      if (extendByMonths === 0 || hasStrictStartDate) {
        return customStartDate;
      }
      
      // Adjust the custom start date if we're allowed to
      const adjustedDate = new Date(customStartDate.getTime());
      adjustedDate.setMonth(adjustedDate.getMonth() - extendByMonths);
      return adjustedDate;
    }
    
    // If we have a forced filter with start date, use that
    const start = activeFilter[StaticFilterKeys.START_DATE];
    if (start && props.forcedFilter && props.forcedStart) {
      if (extendByMonths === 0 || hasStrictStartDate) {
        return start;
      }
      
      // Adjust the forced start date if we're allowed to
      const adjustedDate = new Date(start.getTime());
      adjustedDate.setMonth(adjustedDate.getMonth() - extendByMonths);
      return adjustedDate;
    }
    
    // Default: calculate start date based on end date
    const end = activeFilter[StaticFilterKeys.END_DATE];
    const defaultMonthsBack = 1 + extendByMonths; // At least one month back by default
    return new Date(end.getTime() - (defaultMonthsBack * MONTH_IN_MS));
  }, [getFilter, hasStrictStartDate, props.forcedFilter, props.forcedStart]);

  // Generate a unique key for the current filter state
  const getKey = useCallback((): string => {
    return JSON.stringify({ 
      'cnf': getCnf(), 
      'start': getStartDate(), 
      'end': getFilter()[StaticFilterKeys.END_DATE],
      'is_forced': !!props.forcedFilter
    });
  }, [getCnf, getStartDate, getFilter, props.forcedFilter]);

  // Memoize the current key to ensure stable reference
  const currentKey = useMemo(() => getKey(), [getKey]);

  // Get the enhanced session state combining Redux state with our local enhancements
  const getCurrentSession = useCallback((key: string): ExtendedSessionState => {
    const reduxState = sessionState.sessions[key];
    if (!reduxState) {
      return {
        items: [],
        startRequested: null,
        hasMore: true,
        startHasMore: true,
        isLoading: false,
        error: null,
        lastRequestKey: null,
        totalItems: 0,
        backoffRetryCount: 0,
        noResultsAfterBackoff: false,
        monthsSearched: 1 // Start with 1 month
      };
    }
    
    const enhancement = sessionEnhancementsRef.current.get(key) || { 
      totalItems: reduxState.items.length,
      backoffRetryCount: 0,
      noResultsAfterBackoff: false,
      monthsSearched: 1 // Start with 1 month
    };
    
    return {
      ...reduxState,
      totalItems: enhancement.totalItems,
      backoffRetryCount: enhancement.backoffRetryCount,
      noResultsAfterBackoff: enhancement.noResultsAfterBackoff,
      monthsSearched: enhancement.monthsSearched
    };
  }, [sessionState.sessions]);

  // Memoize the current session state for the key
  const currentSession = useMemo<ExtendedSessionState>(() => 
    getCurrentSession(currentKey),
    [currentKey, getCurrentSession]
  );

  // Update our session enhancement tracking
  const updateSessionEnhancement = useCallback((
    key: string,
    enhancement: { 
      totalItems?: number; 
      backoffRetryCount?: number; 
      noResultsAfterBackoff?: boolean;
      monthsSearched?: number;
    }
  ) => {
    const current = sessionEnhancementsRef.current.get(key) || { 
      totalItems: sessionState.sessions[key]?.items.length || 0,
      backoffRetryCount: 0,
      noResultsAfterBackoff: false,
      monthsSearched: 1
    };
    
    const updated = {
      ...current,
      ...enhancement
    };
    
    sessionEnhancementsRef.current.set(key, updated);
  }, [sessionState.sessions]);

  // Calculate months searched based on actual date range
  const calculateMonthsSearched = useCallback((startDate: Date | null): number => {
    if (!startDate) return 1;
    const endDate = getFilter()[StaticFilterKeys.END_DATE];
    const monthsDiff = (endDate.getTime() - startDate.getTime()) / MONTH_IN_MS;
    return Math.max(1, Math.ceil(monthsDiff));
  }, [getFilter]);

  // Prepare params for API request
  const getParams = useCallback((
    forceRefresh: boolean = false, 
    extendByMonths: number = 0,
    baseStartDate?: Date | null
  ): ListUserSessionParamsV3 => {
    const relevantItems = currentSession.items;
    
    // For normal pagination or force refresh, use the last item's scheduled start or filter end date
    let end;
    if (forceRefresh) {
      end = getFilter()[StaticFilterKeys.END_DATE];
    } else if (extendByMonths > 0 && baseStartDate) {
      // When loading more historical data, use the previous start date as our end date
      end = baseStartDate;
    } else if (relevantItems && relevantItems.length > 0) {
      // Normal pagination - use last item's date
      end = relevantItems[relevantItems.length - 1].scheduled_start;
    } else {
      end = getFilter()[StaticFilterKeys.END_DATE];
    }
      
    return { 
      'cnf': getCnf(), 
      'start': getStartDate(extendByMonths, baseStartDate), 
      'end': end
    };
  }, [currentSession.items, getCnf, getStartDate, getFilter]);

  // Check if a date is within the allowed lookback window
  const isWithinDateLimit = useCallback((date: Date | null): boolean => {
    if (!date) return true; // No date means no limit
    
    const endDate = getFilter()[StaticFilterKeys.END_DATE];
    const maxLookback = MAX_DATE_LOOKBACK_MONTHS * MONTH_IN_MS;
    
    return (endDate.getTime() - date.getTime()) <= maxLookback;
  }, [getFilter]);

  // Check if we need more data based on current item count
  const needsMoreData = useCallback((itemCount: number): boolean => {
    return !hasStrictStartDate && itemCount < MIN_DESIRED_ITEMS;
  }, [hasStrictStartDate]);

  // The main fetch function
  const fetchSessions = useCallback(async (
    options: {
      isLoadMore?: boolean; 
      forceRefresh?: boolean;
      extendByMonths?: number;
      baseStartDate?: Date | null;
      backoffRetryCount?: number;
      isHistoricalSearch?: boolean;
    } = {}
  ): Promise<void> => {
    const { 
      isLoadMore = false, 
      forceRefresh = false,
      extendByMonths = 0,
      baseStartDate = null,
      backoffRetryCount = 0,
      isHistoricalSearch = false
    } = options
    
    // Prevent recursive auto-fetching
    if (!isLoadMore && !forceRefresh && isAutoFetchingRef.current) {
      console.log('[SessionList Hook] Skipping auto-fetch - already in progress');
      return;
    }

    // Generate a unique ID for this fetch operation for tracking
    const operationId = Date.now().toString(36);
    console.log(`[SessionList Hook] Starting fetch operation ${operationId} - isLoadMore: ${isLoadMore}, forceRefresh: ${forceRefresh}`);

    const key = currentKey;
    const currentState = getCurrentSession(key);

    // Don't fetch if we're already loading
    if (currentState.isLoading && !forceRefresh) {
      console.log('[SessionList Hook] Skipping fetch - already loading');
      return;
    }

    // Don't fetch if we've reached the end and this isn't a force refresh or load more
    if (!forceRefresh && !isLoadMore && !currentState.hasMore && currentState.items.length > 0) {
      console.log('[SessionList Hook] Skipping fetch - no more items available');
      return;
    }

    // Calculate the adjusted start date for this request
    let effectiveStartDate = baseStartDate;
    if (!effectiveStartDate && currentState.startRequested) {
      // If we got a full page last time, just continue from where we left off
      // Otherwise, adjust the start date based on the load type
      if (currentState.startHasMore) {
        effectiveStartDate = currentState.startRequested;
      } else if (isLoadMore || backoffRetryCount > 0) {
        effectiveStartDate = currentState.startRequested;
      }
    }
    
    // Calculate the effective extension amount
    let effectiveExtension = extendByMonths;
    if (isHistoricalSearch) {
      // For historical searches, always use LOAD_MORE_MONTHS_EXTENSION
      effectiveExtension = LOAD_MORE_MONTHS_EXTENSION;
    } else if (!currentState.startHasMore) {
      if (backoffRetryCount > 0 && BACKOFF_MULTIPLIERS[backoffRetryCount - 1]) {
        effectiveExtension = BACKOFF_MULTIPLIERS[backoffRetryCount - 1];
      }
    } else {
      effectiveExtension = 0; // Don't extend if we got a full page last time
    }

    // Get params for the API request
    const params = getParams(forceRefresh, effectiveExtension, effectiveStartDate);
    
    console.log(`[SessionList Hook] Fetch operation ${operationId} params:`, {
      start: params.start?.toISOString(),
      end: params.end?.toISOString(),
      gotFullPageLastTime: currentState.startHasMore,
      effectiveExtension
    });

    // Skip if we have no parameters to fetch with
    if (!params.cnf && !params.start && !params.end) {
      console.log('[SessionList Hook] Skipping fetch - no valid parameters');
      return;
    }

    // Skip if the adjusted start date is beyond our limit
    if (params.start && !isWithinDateLimit(params.start)) {
      console.log('[SessionList Hook] Skipping fetch - beyond date limit');
      dispatch(updateHasMore({ key, hasMore: false }));
      return;
    }
    
    // Prevent duplicate requests
    const requestKey = JSON.stringify(params);
    if (currentState.lastRequestKey === requestKey && !forceRefresh) {
      console.log('[SessionList Hook] Skipping fetch - duplicate request');
      return;
    }

    // If force refresh, clear the sessions
    if (forceRefresh) {
      console.log(`[SessionList Hook] Force refresh in operation ${operationId} - clearing sessions`);
      dispatch(clearSessions({ key }));
      // Reset our enhancements tracker for this key
      updateSessionEnhancement(key, { 
        totalItems: 0, 
        backoffRetryCount: 0, 
        noResultsAfterBackoff: false, 
        monthsSearched: 1 
      });
    }

    // Reset noResultsAfterBackoff when loading more data
    if (isLoadMore || isHistoricalSearch) {
      updateSessionEnhancement(key, { 
        noResultsAfterBackoff: false
      });
    }

    try {
      if (!isLoadMore && !forceRefresh) {
        isAutoFetchingRef.current = true;
      }

      // Start loading
      console.log(`[SessionList Hook] Operation ${operationId} - dispatching startLoading`);
      dispatch(startLoading({ key, lastRequestKey: requestKey }));
      
      // Fetch the data
      console.log(`[SessionList Hook] Operation ${operationId} - calling API`);
      const result = await getServicesManager().getListUserSessionsV3(params);
      if (!result) { 
        console.log(`[SessionList Hook] Operation ${operationId} - No result returned from API`);
        return;
      }
      
      console.log(`[SessionList Hook] Operation ${operationId} - API returned ${result.length} items`);
      
      // Calculate total items after this fetch
      const totalItems = forceRefresh 
        ? result.length 
        : (currentState.items.length + result.length);
      
      // Determine if we have more items to fetch
      const resultFull = result.length === DEFAULT_PAGE_SIZE;
      
      // We have more items if either:
      // 1. We got a full page of results (normal pagination)
      // 2. We don't have a strict start date and haven't hit the max lookback period
      // 3. We're still in backoff retries and got results (but only for auto-backoff, not manual loads)
      const nextStartDate = params.start ? new Date(params.start.getTime() - MONTH_IN_MS * (isLoadMore ? 1 : LOAD_MORE_MONTHS_EXTENSION)) : null;
      const canLoadMoreDates = !hasStrictStartDate && nextStartDate && isWithinDateLimit(nextStartDate);
      
      // Check if we've reached the maximum lookback period
      let reachedMaxLookback = false;
      if (params.start) {
        const endDate = getFilter()[StaticFilterKeys.END_DATE];
        const monthsDiff = (endDate.getTime() - params.start.getTime()) / MONTH_IN_MS;
        reachedMaxLookback = monthsDiff >= MAX_DATE_LOOKBACK_MONTHS;
        
        if (reachedMaxLookback) {
          console.log(`[SessionList Hook] Operation ${operationId} - Reached maximum lookback of ${MAX_DATE_LOOKBACK_MONTHS} months`);
        }
      }
      
      // Add reached max lookback check
      const hasMore = resultFull || (canLoadMoreDates && !reachedMaxLookback) || 
                     (!isLoadMore && backoffRetryCount < MAX_BACKOFF_RETRIES && result.length > 0);
      
      console.log(
        `[SessionList Hook] Operation ${operationId} results: got ${result.length} items, total ${totalItems}, ` +
        `hasMore: ${hasMore}, backoffRetry: ${backoffRetryCount}, ` +
        `canLoadMoreDates: ${canLoadMoreDates}, strictStartDate: ${hasStrictStartDate}`
      );
      
      // Update Redux state with the new data
      console.log(`[SessionList Hook] Operation ${operationId} - dispatching sessionsLoaded`);
      dispatch(sessionsLoaded({ 
        key,
        items: forceRefresh ? result : [...(currentState.items || []), ...result],
        startRequested: params.start ? new Date(params.start) : null,
        startHasMore: resultFull,
        hasMore: hasMore,
        isForceRefresh: forceRefresh, 
        lastRequestKey: requestKey
      }));
      
      // Update our local enhancement tracking
      const shouldShowNoResults = 
        totalItems === 0 && 
        backoffRetryCount > 0 && 
        !hasStrictStartDate && 
        !isLoadMore;

      // Calculate actual months searched based on the date range
      const actualMonthsSearched = calculateMonthsSearched(params.start);

      updateSessionEnhancement(key, {
        totalItems,
        backoffRetryCount: Math.max(currentState.backoffRetryCount || 0, backoffRetryCount),
        noResultsAfterBackoff: shouldShowNoResults,
        monthsSearched: actualMonthsSearched
      });
      
      // Only auto-fetch more if:
      // 1. We need more data (less than MIN_DESIRED_ITEMS)
      // 2. Haven't maxed out backoff retries
      // 3. Either:
      //    a. Got 0 items in this request and trying a backoff retry
      //    b. Got items but less than a full page (not resultFull)
      const isInitialRequest = !isLoadMore && !forceRefresh;
      if (isInitialRequest && 
          needsMoreData(totalItems) && 
          backoffRetryCount < MAX_BACKOFF_RETRIES && 
          !hasStrictStartDate &&
          (result.length === 0 || !resultFull)) {
        // Recalculate with the new data
        const newCurrentState = getCurrentSession(key);
        const shouldFetchMore = needsMoreData(newCurrentState.items.length);

        if (shouldFetchMore) {
          const newBackoffCount = backoffRetryCount + 1;
          console.log(`[SessionList Hook] Operation ${operationId} - Auto-fetching more data, backoff retry ${newBackoffCount}`);
          
          // Wait longer for each retry to prevent spam
          const backoffDelay = 250 * Math.pow(2, backoffRetryCount);
          setTimeout(() => {
            fetchSessions({ 
              forceRefresh: false, 
              isLoadMore: false,
              extendByMonths: BACKOFF_MULTIPLIERS[backoffRetryCount] || 1,
              baseStartDate: params.start,
              backoffRetryCount: newBackoffCount
            });
          }, backoffDelay);
        } else {
          console.log(`[SessionList Hook] Operation ${operationId} - No need for more data, have ${newCurrentState.items.length} items`);
        }
      }
      
      console.log(`[SessionList Hook] Operation ${operationId} completed successfully`);
    } catch (error) {
      console.error(`[SessionList Hook] Operation ${operationId} error:`, error);
      dispatch(loadingFailed({ 
        key, 
        error: error instanceof Error ? error.message : 'Unknown error fetching sessions' 
      }));
    } finally {
      if (!isLoadMore && !forceRefresh) {
        isAutoFetchingRef.current = false;
      }
      console.log(`[SessionList Hook] Operation ${operationId} finalized`);
    }
  }, [
    currentKey, 
    getCnf,
    getFilter,
    hasStrictStartDate,
    getCurrentSession,
    getParams,
    isWithinDateLimit,
    dispatch,
    needsMoreData,
    updateSessionEnhancement,
    calculateMonthsSearched
  ]);

  // Handle initial load and filter changes
  useEffect(() => {
    // Skip if we're already auto-fetching
    if (isAutoFetchingRef.current) {
      return;
    }

    // On initial mount, just do a normal fetch
    // On subsequent runs (filter changes), only fetch if filter has actually changed
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchSessions({ forceRefresh: false });
    } else if (hasFilterChanged.current) {
      fetchSessions({ forceRefresh: true });
      hasFilterChanged.current = false;
    }
  }, [currentKey, fetchSessions]);

  // Track filter changes
  useEffect(() => {
    // Skip filter change detection on initial mount
    if (isInitialMount.current) {
      return;
    }

    const currentFilterString = JSON.stringify(filter);
    if (previousFilterRef.current !== currentFilterString) {
      console.log('[SessionList Hook] Filter changed, will trigger refresh on next render');
      previousFilterRef.current = currentFilterString;
      hasFilterChanged.current = true;
    }
  }, [filter]);

  // Handle force refresh (e.g., from refresh button)
  useEffect(() => {
    // Skip if we have a forced filter or if refresh count hasn't changed
    if (refreshCount === 0) return;
    
    // Skip if we've already processed this refresh count
    if (refreshCount === lastProcessedRefreshRef.current) {
      console.log('[SessionList Hook] Ignoring duplicate refresh trigger:', refreshCount);
      return;
    }
    
    // Skip if we're already in the process of refreshing
    if (isRefreshingRef.current) {
      console.log('[SessionList Hook] Refresh already in progress, skipping:', refreshCount);
      lastProcessedRefreshRef.current = refreshCount; // Still mark as processed
      return;
    }
    
    console.log('[SessionList Hook] Manual refresh triggered', refreshCount);
    lastProcessedRefreshRef.current = refreshCount;
    
    // Set flag to indicate we're refreshing
    isRefreshingRef.current = true;
    
    // Execute the refresh
    fetchSessions({ forceRefresh: true })
      .finally(() => {
        // Reset refreshing flag when done
        setTimeout(() => {
          isRefreshingRef.current = false;
          console.log('[SessionList Hook] Refresh completed, reset flag');
        }, 500);
      });
  }, [refreshCount, fetchSessions, props.forcedFilter]);

  // Public methods
  const loadMore = useCallback((fromInfiniteScroll: boolean = false) => {
    console.log('[SessionList Hook] Load more called, from infinite scroll:', fromInfiniteScroll);
    
    // If this is a historical search (not from infinite scroll), add more extensive logging
    if (!fromInfiniteScroll) {
      const currentState = getCurrentSession(currentKey);
      const currentMonthsSearched = currentState.monthsSearched || 1;
      
      console.log(`[SessionList Hook] Historical search triggered - current months searched: ${currentMonthsSearched}`);
      
      // Check if we're already at or beyond the maximum lookback months
      if (currentMonthsSearched >= MAX_DATE_LOOKBACK_MONTHS) {
        console.log(`[SessionList Hook] Already at maximum lookback (${MAX_DATE_LOOKBACK_MONTHS} months), can't load more historical data`);
        return; // Exit early
      }
      
      // Calculate how many more months we'll load
      const nextMonthsSearched = Math.min(currentMonthsSearched + LOAD_MORE_MONTHS_EXTENSION, MAX_DATE_LOOKBACK_MONTHS);
      console.log(`[SessionList Hook] Will extend search by ${LOAD_MORE_MONTHS_EXTENSION} more months (${nextMonthsSearched} total)`);
    }
    
    // For historical searches, use a more aggressive approach and use larger extension
    const extendByMonths = fromInfiniteScroll ? 1 : LOAD_MORE_MONTHS_EXTENSION;
    
    // Set up a timeout to show loading state for a minimum time for better UX
    const minimumLoadingTime = 500; // 500ms minimum loading time for better UX
    const startTime = Date.now();
    
    fetchSessions({ 
      isLoadMore: true, 
      extendByMonths: extendByMonths,
      // Don't count infinite scroll loads against backoff retries
      backoffRetryCount: 0,
      // Mark as historical search if not from infinite scroll
      isHistoricalSearch: !fromInfiniteScroll
    }).finally(() => {
      // Ensure we show loading for at least the minimum time
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime < minimumLoadingTime) {
        setTimeout(() => {
          console.log(`[SessionList Hook] Artificial delay for better UX (${minimumLoadingTime - elapsedTime}ms)`);
        }, minimumLoadingTime - elapsedTime);
      }
    });
  }, [fetchSessions, currentKey, getCurrentSession]);

  const refresh = useCallback(() => {
    console.log('[SessionList Hook] Refresh method called directly');
    if (isRefreshingRef.current) {
      console.log('[SessionList Hook] Already refreshing, skipping direct refresh call');
      return;
    }
    
    isRefreshingRef.current = true;
    fetchSessions({ forceRefresh: true })
      .finally(() => {
        // Reset refreshing flag when done
        setTimeout(() => {
          isRefreshingRef.current = false;
          console.log('[SessionList Hook] Direct refresh completed, reset flag');
        }, 500);
      });
  }, [fetchSessions]);

  return {
    items: currentSession.items || [],
    isLoading: currentSession.isLoading,
    hasMore: currentSession.hasMore,
    error: currentSession.error,
    loadMore,
    refresh,
    totalItems: currentSession.totalItems || currentSession.items.length,
    noResultsAfterBackoff: currentSession.noResultsAfterBackoff,
    monthsSearched: currentSession.monthsSearched || 1,
    hasStrictStartDate
  };
}
